import {
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  type Theme,
} from '@mui/material';
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import {
  adminTheme,
  AgronodButton,
  AgronodLogo,
  AgronodTypography,
  AgrosfarLogo,
  ThemeProvider,
} from '@agronod/mui-components';
import { useAuth } from 'oidc-react';
import { Link, useLocation } from 'react-router-dom';
import { config } from '../config';
import { useOidcUser } from '../utils/hooks';

// Pillerformade poster (designprototyp v4): aktiv post i primary.medium,
// hover som svag vit ton.
const navItemSx = (theme: Theme) => ({
  color: theme.palette.white,
  borderRadius: '64px',
  px: 2,
  py: 1.5,
  // ListItemButton har flexGrow: 1 som standard – i kolumnlistan skulle
  // knapparna annars växa och dela på hela höjden.
  flexGrow: 0,
  '& .MuiListItemIcon-root': { color: theme.palette.white, minWidth: 36 },
  ':hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
  '&.Mui-selected, &.Mui-selected:hover': {
    backgroundColor: theme.palette.primary.medium,
  },
  '&:focus-visible': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
});

// Logotyperna är flerfärgade – enfärgat vita mot den mörka menyn. Alla
// element är <path>, så regeln täcker hela märket.
const whiteLogoSx = (theme: Theme) => ({
  display: 'flex',
  '& svg': { height: 'auto' },
  '& svg path': { fill: theme.palette.white },
});

/**
 * Innehållet i sidomenyn – används både av den permanenta menyn (desktop)
 * och den tillfälliga (mobil) i AppLayout. Menyn renderas i Admin-temat
 * (den vinröda skalan som primary; `tertiary` togs bort i mui-components
 * 3.0, se docs/migrations/remove-tertiary.md i det repot) medan resten av
 * appen behåller Agronod-temat. Länken till användaradministration
 * går till b2b-konto-web där revisionsledare tilldelar roller till kontots
 * användare (se docs/uppsattning-anvandaratkomst.md).
 */
const SideMenu = ({ onNavigate }: { onNavigate?: () => void }) => {
  const auth = useAuth();
  const { oidcUser } = useOidcUser();
  const { pathname } = useLocation();

  return (
    <ThemeProvider options={adminTheme}>
    <Box
      sx={(theme) => ({
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.white,
        borderTopRightRadius: '24px',
        px: 1.75,
        pt: 4,
        pb: 3,
      })}
    >
      <Box sx={{ px: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <AgronodTypography
          variant="overline"
          sx={(theme) => ({ color: theme.palette.white, letterSpacing: 0.5 })}
        >
          Verifieringsapplikation
        </AgronodTypography>
        <Box sx={(theme) => ({ ...whiteLogoSx(theme), '& svg': { width: 148, height: 'auto' } })}>
          <AgrosfarLogo />
        </Box>
      </Box>

      <List
        sx={{
          mt: 4.5,
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <ListItemButton
          component={Link}
          to="/"
          selected={pathname === '/'}
          onClick={onNavigate}
          sx={navItemSx}
        >
          <ListItemIcon>
            <FormatListBulletedRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Verifieringsuppdrag"
            slotProps={{
              primary: { variant: 'subtitle2' }
            }}
          />
        </ListItemButton>
        {/* b2b-konto-web läser ?redirect= och visar en tillbaka-länk hit,
            märkt med ?redirectName= (useBackLink, allowlistad till
            *.agronod.com/localhost). */}
        <ListItemButton
          component="a"
          href={`${config.KONTO_ADMIN_URL}?redirect=${encodeURIComponent(
            window.location.origin + pathname,
          )}&redirectName=${encodeURIComponent('Agrosfär Verifiering')}`}
          sx={navItemSx}
        >
          <ListItemIcon>
            <ManageAccountsOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Kontohantering"
            slotProps={{
              primary: { variant: 'subtitle2' }
            }}
          />
        </ListItemButton>
      </List>

      <Divider
        sx={(theme) => ({ borderColor: theme.palette.primary.medium, mb: 3 })}
      />
      <Box sx={{ px: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <AgronodTypography
          variant="overline"
          sx={(theme) => ({
            color: theme.palette.primary.light,
            letterSpacing: 0.5,
          })}
        >
          Inloggad som
        </AgronodTypography>
        <AgronodTypography variant="body2" sx={{ fontWeight: 500 }}>
          {oidcUser?.name || oidcUser?.email}
        </AgronodTypography>
        {oidcUser?.name && (
          <AgronodTypography
            variant="caption"
            sx={(theme) => ({
              color: theme.palette.primary.light,
              wordBreak: 'break-all',
            })}
          >
            {oidcUser.email}
          </AgronodTypography>
        )}
        <AgronodButton
          variant="contained"
          size="small"
          fullWidth
          onClick={() => auth.signOutRedirect()}
          sx={(theme) => ({
            mt: 2.5,
            color: theme.palette.black,
            backgroundColor: theme.palette.primary.light,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: theme.palette.white,
              boxShadow: 'none',
            },
          })}
        >
          Logga ut
        </AgronodButton>
      </Box>

      <Divider
        sx={(theme) => ({ borderColor: theme.palette.primary.medium, mt: 3, mb: 2.5 })}
      />
      <Box sx={{ px: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <AgronodTypography
          variant="overline"
          sx={(theme) => ({
            color: theme.palette.primary.light,
            letterSpacing: 0.5,
            fontSize: 10,
            lineHeight: '14px',
          })}
        >
          Drivs av
        </AgronodTypography>
        <Box sx={(theme) => ({ ...whiteLogoSx(theme), opacity: 0.85, '& svg': { height: 18, width: 'auto' } })}>
          <AgronodLogo />
        </Box>
      </Box>
    </Box>
    </ThemeProvider>
  );
};

export default SideMenu;
